import DashboardUserMenu from '../components/DashboardUserMenu';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Button, Table, Form, Modal, Offcanvas, Container,
} from 'react-bootstrap';
import { Dropdown, ButtonGroup } from 'react-bootstrap';
import './SuperuserDashboard.css';
import { API_URL } from '../config'; // adjust path as needed
import { toast } from 'react-toastify';
import { formatPhone } from '../utils/formatPhone';



const SuperuserDashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [markazList, setMarkazList] = useState([]);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    role: 'teacher',
    markaz_id: '' // ✅ required
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [editUserModal, setEditUserModal] = useState({ show: false, user: null });
  const [editUserForm, setEditUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    role: 'teacher',
    markaz_id: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [attentionFilter, setAttentionFilter] = useState('');
  const [accessUser, setAccessUser] = useState(null);
  const [accessSaving, setAccessSaving] = useState(false);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState({ show: false, user: null });
  const [markazModal, setMarkazModal] = useState({ show: false, user: null, markaz_id: '' });
  const sessionExpiredRef = useRef(false);
  

  const generatePassword = () => Math.random().toString(36).slice(-10);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Password copied to clipboard');
  };

  const handleSessionExpired = () => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    toast.error('Your session expired. Please log in again.');
    logout();
    navigate('/login');
  };

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'superuser') {
      navigate('/login');
    } else {
      fetchUsers();
      fetchMarkazList();
      setFormData(prev => ({ ...prev, password: generatePassword() }));
      setLoading(false);
    }
  }, [navigate, currentUser]);
  
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      const data = await response.json();
      if (response.ok) setUsers(data);
      else throw new Error(data.error);
    } catch (error) {
      console.error("❌ Error fetching users:", error);
      setError('Failed to fetch users');
    }
  };
  const fetchMarkazList = async () => {
    try {
      const response = await fetch(`${API_URL}/markaz`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
      const data = await response.json();
  
      if (response.ok) {
        setMarkazList(data);
        // ✅ Set default markaz_id if it's not already set and data is available
        setFormData(prev => ({
          ...prev,
          markaz_id: prev.markaz_id || (data.length > 0 ? data[0].id : '')
        }));
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error("❌ Error fetching markaz list:", error);
    }
  };
  
  

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const { first_name, last_name, email, password, role, phone_number, markaz_id } = formData;
  
    if (!first_name || !last_name || !email || !password || !role || !markaz_id) {
      setError('All fields including Markaz are required');
      return;
    }
  
    try {
      const response = await fetch(`${API_URL}/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          first_name,
          last_name,
          email,
          password,
          role,
          phone_number,
          markaz_id: parseInt(markaz_id, 10)  // ✅ ensure it's a number
        })
      });
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
  
      const data = await response.json();
  
      if (!response.ok) throw new Error(data.error || "User creation failed");
  
      toast.success('User created successfully');
      fetchUsers();
      setShowModal(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        password: generatePassword(),
        role: 'teacher',
        markaz_id: ''
      });
  
    } catch (error) {
      console.log("❌ Error during user creation:", error);
      setError('Error creating user');
    }
  };
  
  
  

  const handleUpdateRole = async (id, newRole) => {
    try {
      const response = await fetch(`${API_URL}/users/${id}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ role: newRole }),
      });
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }
  
      if (response.ok) {
        toast.success(`✅ User role updated to ${newRole}`);
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(`❌ Failed to update role: ${data.error || 'Server error'}`);
      }
    } catch (error) {
      console.error('❌ Error updating role:', error);
      toast.error('❌ Network error while updating role');
    }
  };

  const handleUpdateAccess = async () => {
    if (!accessUser || accessSaving) return;
    setAccessSaving(true);
    try {
      const response = await fetch(`${API_URL}/users/${accessUser.id}/access`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_disabled: !accessUser.is_disabled }),
      });
      if (response.status === 401) { handleSessionExpired(); return; }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not update access');
      setUsers(current => current.map(user => user.id === accessUser.id ? { ...user, is_disabled: data.is_disabled } : user));
      toast.success(data.is_disabled ? 'User disabled' : 'User enabled');
      setAccessUser(null);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAccessSaving(false);
    }
  };

  const handleDeleteUser = (user) => {
    setConfirmDeleteUser({ show: true, user });
  };

  const handleConfirmDeleteUser = async () => {
    const user = confirmDeleteUser.user;
    if (!user) return;

    try {
      const response = await fetch(`${API_URL}/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      if (response.ok) {
        toast.success('User deleted successfully');
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error('Network error while deleting user');
    } finally {
      setConfirmDeleteUser({ show: false, user: null });
    }
  };

  const handleOpenMarkazModal = (user) => {
    setMarkazModal({
      show: true,
      user,
      markaz_id: user?.markaz_id ? String(user.markaz_id) : ''
    });
  };

  const handleOpenEditUser = (user) => {
    setEditUserForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      role: user.role || 'teacher',
      markaz_id: user.markaz_id ? String(user.markaz_id) : ''
    });
    setEditUserModal({ show: true, user });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editUserModal.user) return;

    try {
      const response = await fetch(`${API_URL}/users/${editUserModal.user.id}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editUserForm)
      });

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user');
      }

      toast.success('User updated');
      setEditUserModal({ show: false, user: null });
      fetchUsers();
    } catch (error) {
      console.error('❌ Error updating user:', error);
      toast.error(error.message || 'Failed to update user');
    }
  };

  const handleUpdateMarkaz = async () => {
    if (!markazModal.user?.id || !markazModal.markaz_id) return;

    try {
      const response = await fetch(`${API_URL}/users/${markazModal.user.id}/markaz`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ markaz_id: parseInt(markazModal.markaz_id, 10) }),
      });
      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      if (response.ok) {
        toast.success('User markaz updated successfully');
        fetchUsers();
        setMarkazModal({ show: false, user: null, markaz_id: '' });
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to update markaz');
      }
    } catch (error) {
      console.error('Update markaz error:', error);
      toast.error('Network error while updating markaz');
    }
  };
  

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const disabledUsers = users.filter((user) => Boolean(user.is_disabled));
  const usersNeverLoggedIn = users.filter((user) => !user.last_login_at);
  const recentCutoff = Date.now() - (7 * 24 * 60 * 60 * 1000);
  const recentlyCreatedUsers = users.filter((user) => {
    const createdAt = new Date(user.created_at).getTime();
    return Number.isFinite(createdAt) && createdAt >= recentCutoff;
  });
  const markazWithoutAdmin = markazList.filter((markaz) => !users.some((user) =>
    String(user.markaz_id) === String(markaz.id) && user.role === 'admin' && !user.is_disabled
  ));
  const markazWithoutAdminIds = new Set(markazWithoutAdmin.map((markaz) => String(markaz.id)));
  const usersForAttentionFilter = users.filter((user) => {
    if (attentionFilter === 'disabled') return Boolean(user.is_disabled);
    if (attentionFilter === 'never-logged-in') return !user.last_login_at;
    if (attentionFilter === 'recent') return recentlyCreatedUsers.some((recentUser) => recentUser.id === user.id);
    if (attentionFilter === 'no-admin') return markazWithoutAdminIds.has(String(user.markaz_id));
    return true;
  });
  const attentionFilterLabels = {
    disabled: 'Disabled users',
    'never-logged-in': 'Users who have never logged in',
    recent: 'Recently created users',
    'no-admin': 'Markaz locations without an active admin',
  };
  const toggleAttentionFilter = (filter) => setAttentionFilter((current) => current === filter ? '' : filter);
  const query = searchTerm.trim().toLowerCase();
  const filteredUsers = usersForAttentionFilter.filter(user =>
    [`${user.first_name || ''} ${user.last_name || ''}`, user.email, user.role, user.markaz_name]
      .some(value => String(value || '').toLowerCase().includes(query))
  );
  const overview = [
    { label: 'Total users', value: users.length, icon: 'people' },
    { label: 'Admins', value: users.filter(user => user.role === 'admin').length, icon: 'shield-check' },
    { label: 'Teachers', value: users.filter(user => user.role === 'teacher').length, icon: 'person-workspace' },
    { label: 'Markaz', value: markazList.length, icon: 'buildings' },
  ];

  if (loading) return <p className="text-center fw-bold mt-5">Loading...</p>;

  return (
    <div className="d-flex superuser-dashboard-wrapper">
      {/* Sidebar */}
      <Offcanvas
  show={sidebarOpen}
  onHide={() => setSidebarOpen(false)}
  placement="start"
  responsive="lg"
  className="sidebar"
>
  <Offcanvas.Header closeButton className="text-white">
    <Offcanvas.Title>Menu</Offcanvas.Title>
  </Offcanvas.Header>

  <Offcanvas.Body className="d-flex flex-column gap-2 overflow-auto">
    <Button variant="outline-primary" onClick={() => setShowModal(true)}>
      <i className="bi bi-person-plus me-2"></i> Create New User
    </Button>

    <Button variant="outline-secondary" onClick={() => setSidebarOpen(false)}>
      <i className="bi bi-people me-2"></i> All Users
    </Button>

    <Button variant="outline-info" onClick={() => setSidebarOpen(false)}>
      <i className="bi bi-bar-chart-line me-2"></i> Reporting
    </Button>

    <Button
      variant="outline-warning"
      onClick={() => {
        navigate('/reset-password');
        setSidebarOpen(false);
      }}
    >
      <i className="bi bi-lock me-2"></i> Reset User Password
    </Button>

    <Button variant="outline-danger" className="dashboard-logout-btn" onClick={handleLogout}>
      <i className="bi bi-box-arrow-right me-2"></i> Logout
    </Button>
  </Offcanvas.Body>
</Offcanvas>



      <Modal show={!!accessUser} onHide={() => !accessSaving && setAccessUser(null)} centered>
        <Modal.Header closeButton={!accessSaving}><Modal.Title>{accessUser?.is_disabled ? 'Enable user' : 'Disable user'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>{accessUser?.first_name} {accessUser?.last_name} ({accessUser?.email})</p>
          {accessUser?.is_disabled ? 'This user will be able to sign in again.' : 'This user will lose access, including from existing sessions. Their records will be kept.'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={accessSaving} onClick={() => setAccessUser(null)}>Cancel</Button>
          <Button variant={accessUser?.is_disabled ? 'success' : 'danger'} disabled={accessSaving} onClick={handleUpdateAccess}>
            {accessSaving ? 'Saving…' : accessUser?.is_disabled ? 'Enable user' : 'Disable user'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Container className="py-4 superuser-content">
        <div className="d-flex justify-content-between align-items-center mb-3 superuser-header">
          <div>
            <h1 className="mb-1">Superuser Dashboard</h1>
            <div className="text-muted small">Manage users and markaz access</div>
          </div>
          <Button
            variant="outline-dark"
            className="d-lg-none"
            aria-label="Open navigation menu"
            onClick={() => setSidebarOpen(true)}
          >
            <i className="bi bi-list"></i>
          </Button>
          <DashboardUserMenu />

        </div>

        <section className="superuser-welcome" aria-label="Welcome">
          <div>
            <span className="superuser-eyebrow">Your platform at a glance</span>
            <h2>Welcome back, {currentUser?.name || 'Superuser'} <span aria-hidden="true">👋</span></h2>
            <p>Manage your team and keep access organized across every markaz.</p>
          </div>
          <div className="superuser-welcome-date">
            <i className="bi bi-calendar3" aria-hidden="true" />
            {new Date().toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </section>
        <div className="superuser-overview">
          {overview.map(item => (
            <div className="superuser-stat" key={item.label}>
              <i className={`bi bi-${item.icon}`} aria-hidden="true" />
              <div><span>{item.label}</span><strong>{item.value}</strong></div>
            </div>
          ))}
        </div>

        <section className="superuser-attention" aria-labelledby="superuser-attention-title">
          <div className="superuser-attention__heading">
            <div>
              <span>Platform checks</span>
              <h2 id="superuser-attention-title">Needs attention</h2>
              <p>Select a card to review the related users below.</p>
            </div>
          </div>
          <div className="superuser-attention__grid">
            <button type="button" className={`superuser-attention-card superuser-attention-card--danger${attentionFilter === 'disabled' ? ' is-active' : ''}`} aria-pressed={attentionFilter === 'disabled'} onClick={() => toggleAttentionFilter('disabled')}>
              <i className="bi bi-person-slash" aria-hidden="true" />
              <span><strong>{disabledUsers.length}</strong><b>Disabled users</b><small>{disabledUsers.length ? 'Review or restore account access' : 'No disabled accounts'}</small></span>
            </button>
            <button type="button" className={`superuser-attention-card superuser-attention-card--warning${attentionFilter === 'no-admin' ? ' is-active' : ''}`} aria-pressed={attentionFilter === 'no-admin'} onClick={() => toggleAttentionFilter('no-admin')}>
              <i className="bi bi-building-exclamation" aria-hidden="true" />
              <span><strong>{markazWithoutAdmin.length}</strong><b>Markaz without an admin</b><small>{markazWithoutAdmin.length ? markazWithoutAdmin.slice(0, 2).map((markaz) => markaz.name).join(', ') : 'Every Markaz has an active admin'}</small></span>
            </button>
            <button type="button" className={`superuser-attention-card${attentionFilter === 'never-logged-in' ? ' is-active' : ''}`} aria-pressed={attentionFilter === 'never-logged-in'} onClick={() => toggleAttentionFilter('never-logged-in')}>
              <i className="bi bi-box-arrow-in-right" aria-hidden="true" />
              <span><strong>{usersNeverLoggedIn.length}</strong><b>Users who never logged in</b><small>Login activity has not been recorded</small></span>
            </button>
            <button type="button" className={`superuser-attention-card superuser-attention-card--new${attentionFilter === 'recent' ? ' is-active' : ''}`} aria-pressed={attentionFilter === 'recent'} onClick={() => toggleAttentionFilter('recent')}>
              <i className="bi bi-person-plus" aria-hidden="true" />
              <span><strong>{recentlyCreatedUsers.length}</strong><b>Recently created users</b><small>Created within the last 7 days</small></span>
            </button>
          </div>
        </section>

        <div className="superuser-directory-heading">
          <div>
            <h2>Users</h2>
            <span className="text-muted small" role="status">{filteredUsers.length} of {users.length} users</span>
            {attentionFilter && (
              <button type="button" className="superuser-filter-chip" onClick={() => setAttentionFilter('')}>
                {attentionFilterLabels[attentionFilter]} <i className="bi bi-x" aria-hidden="true" />
              </button>
            )}
          </div>
          <Button className="superuser-create-btn" onClick={() => setShowModal(true)}>
            <i className="bi bi-person-plus me-2" aria-hidden="true" />Create User
          </Button>
        </div>

        <div className="superuser-search mb-3">
          <Form.Control
            type="text"
            placeholder="Search by name, email, role, or markaz…"
            aria-label="Search users by name, email, role, or markaz"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {error && <p className="text-danger">{error}</p>}

        {!filteredUsers.length && (
          <div className="superuser-empty" role="status">
            <h3>{query ? 'No matching users' : 'No users yet'}</h3>
            <p>{query ? 'Try a different name, email, role, or markaz.' : 'Create a user to get started.'}</p>
            {query && <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>Clear search</Button>}
          </div>
        )}

        {/* Table for medium and up */}
        <div className="d-none d-md-block">
          <Table striped bordered hover responsive className="superuser-table">
          <thead className="table-light">
            <tr>
              <th>First</th>
              <th>Last</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th><th>Status</th>
              <th>Markaz</th> {/* ✅ Added */}
              <th className="text-center">Actions</th>
            </tr>
          </thead>

            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.first_name}</td>
                  <td>{user.last_name}</td>
                  <td>{user.email}</td>
                  <td>{formatPhone(user.phone_number) || 'N/A'}</td>
                  <td>{user.role}</td><td><span className={`badge ${user.is_disabled ? 'bg-danger' : 'bg-success'}`}>{user.is_disabled ? 'Disabled' : 'Active'}</span></td>
                  <td>{user.markaz_name || '—'}</td>
                  <td className="text-center">
                    {user.role !== 'superuser' && (
                      <>
                        <Dropdown as={ButtonGroup}>
                      <Dropdown.Toggle size="sm" variant="outline-secondary">
                        Change Role
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleOpenEditUser(user)}>
                          Edit User
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => handleUpdateRole(user.id, 'teacher')}>
                          Make Teacher
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleUpdateRole(user.id, 'admin')}>
                          Make Admin
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleOpenMarkazModal(user)}>
                          Change Markaz
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => setAccessUser(user)}>{user.is_disabled ? 'Enable User' : 'Disable User'}</Dropdown.Item>
                        <Dropdown.Item
                          className="text-danger"
                          onClick={() => handleDeleteUser(user)}
                        >
                          Delete User
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Cards for small devices */}
        <div className="d-block d-md-none">
          {filteredUsers.map(user => (
            <div key={user.id} className="superuser-user-card">
              <div className="superuser-user-card__header">
                <div>
                  <div className="superuser-user-card__name">
                    {user.first_name} {user.last_name}
                  </div>
                  <div className="superuser-user-card__meta">{user.email}</div>
                </div>
                <div><span className="superuser-user-card__role">{user.role}</span><span className={`badge ms-2 ${user.is_disabled ? 'bg-danger' : 'bg-success'}`}>{user.is_disabled ? 'Disabled' : 'Active'}</span></div>
              </div>
              <div className="superuser-user-card__grid">
                <div>
                  <div className="superuser-user-card__label">Phone</div>
                  <div className="superuser-user-card__value">{formatPhone(user.phone_number) || 'N/A'}</div>
                </div>
                <div>
                  <div className="superuser-user-card__label">Markaz</div>
                  <div className="superuser-user-card__value">{user.markaz_name || '—'}</div>
                </div>
              </div>
              {user.role !== 'superuser' && (
                <div className="superuser-user-card__actions">
                  <Dropdown className="superuser-actions-dropdown" align="end">
                    <Dropdown.Toggle size="sm" variant="outline-dark">
                      Actions
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="superuser-actions-menu">
                      <Dropdown.Item onClick={() => handleOpenEditUser(user)}>
                        Edit User
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={() => handleUpdateRole(user.id, 'admin')}>
                        Make Admin
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleUpdateRole(user.id, 'teacher')}>
                        Make Teacher
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleOpenMarkazModal(user)}>
                        Change Markaz
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={() => setAccessUser(user)}>{user.is_disabled ? 'Enable User' : 'Disable User'}</Dropdown.Item>
                      <Dropdown.Item className="text-danger" onClick={() => handleDeleteUser(user)}>
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              )}
            </div>
          ))}
        </div>
      </Container>

      {/* Modal for Create User */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <p className="text-danger">{error}</p>}
          <Form onSubmit={handleCreateUser}>
            <Form.Group className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control type="text" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: formatPhone(e.target.value) })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Password{' '}
                <i className="bi bi-clipboard ms-2" role="button" title="Copy Password" onClick={() => copyToClipboard(formData.password)}></i>
              </Form.Label>
              <Form.Control type="text" value={formData.password} readOnly />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                <option value="teacher">Teacher</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Assign Markaz</Form.Label>
              <Form.Select
                value={formData.markaz_id}
                onChange={(e) => setFormData({ ...formData, markaz_id: e.target.value })}
                required
              >
                <option value="">-- Select Markaz --</option>
                {markazList.map(markaz => (
                  <option key={markaz.id} value={markaz.id}>{markaz.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Button type="submit" variant="success" className="w-100">Create User</Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal for Edit User */}
      <Modal show={editUserModal.show} onHide={() => setEditUserModal({ show: false, user: null })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdateUser}>
            <Form.Group className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                value={editUserForm.first_name}
                onChange={(e) => setEditUserForm({ ...editUserForm, first_name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={editUserForm.last_name}
                onChange={(e) => setEditUserForm({ ...editUserForm, last_name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                value={editUserForm.phone_number}
                onChange={(e) => setEditUserForm({ ...editUserForm, phone_number: formatPhone(e.target.value) })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={editUserForm.role}
                onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
                disabled={editUserModal.user?.role === 'superuser'}
              >
                <option value="teacher">Teacher</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Markaz</Form.Label>
              <Form.Select
                value={editUserForm.markaz_id}
                onChange={(e) => setEditUserForm({ ...editUserForm, markaz_id: e.target.value })}
                disabled={editUserModal.user?.role === 'superuser'}
              >
                <option value="">-- Select Markaz --</option>
                {markazList.map(markaz => (
                  <option key={markaz.id} value={markaz.id}>{markaz.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setEditUserModal({ show: false, user: null })}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={confirmDeleteUser.show}
        onHide={() => setConfirmDeleteUser({ show: false, user: null })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete{' '}
          {confirmDeleteUser.user
            ? `${confirmDeleteUser.user.first_name} ${confirmDeleteUser.user.last_name}`
            : 'this user'}
          ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDeleteUser({ show: false, user: null })}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDeleteUser}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={markazModal.show}
        onHide={() => setMarkazModal({ show: false, user: null, markaz_id: '' })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Markaz</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Markaz</Form.Label>
            <Form.Select
              value={markazModal.markaz_id}
              onChange={(e) =>
                setMarkazModal(prev => ({ ...prev, markaz_id: e.target.value }))
              }
            >
              <option value="">-- Select Markaz --</option>
              {markazList.map(markaz => (
                <option key={markaz.id} value={markaz.id}>{markaz.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setMarkazModal({ show: false, user: null, markaz_id: '' })}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleUpdateMarkaz} disabled={!markazModal.markaz_id}>
            Update Markaz
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SuperuserDashboard;
